import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

const YM = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Editor for a role's or education entry's start/end months (#1441). Same
 * contract as FieldEditorComponent (dumb: no HTTP, no op shapes), but the
 * value is structured: start is required, end is null while "current" is
 * ticked. Validation mirrors HumanEditRequest so a refused save is never
 * reachable from a well-behaved client.
 */
@Component({
  selector: 'bst-period-editor',
  templateUrl: './period-editor.component.html',
  styleUrls: ['./field-editor.component.scss'],
  standalone: false,
})
export class PeriodEditorComponent implements OnChanges {
  @Input() public label = '';
  @Input() public start: string | null = null;
  @Input() public end: string | null = null;
  @Input() public busy = false;

  @Output() public save = new EventEmitter<{ start: string; end: string | null }>();
  @Output() public cancel = new EventEmitter<void>();
  @Output() public dirtyChange = new EventEmitter<boolean>();

  public draftStart = '';
  public draftEnd = '';
  public current = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['start'] || changes['end']) {
      this.draftStart = this.start ?? '';
      this.draftEnd = this.end ?? '';
      this.current = this.end === null;
      this.dirtyChange.emit(false);
    }
  }

  public get effectiveEnd(): string | null {
    return this.current ? null : (this.draftEnd || null);
  }

  public get isDirty(): boolean {
    return this.draftStart !== (this.start ?? '') || this.effectiveEnd !== this.end;
  }

  /** i18n suffix under cv.tailor.edit.period*, or null when the draft is valid. */
  public get error(): 'startRequired' | 'endInvalid' | 'endBeforeStart' | null {
    if (!YM.test(this.draftStart)) { return 'startRequired'; }
    const end = this.effectiveEnd;
    if (end !== null && !YM.test(end)) { return 'endInvalid'; }
    if (end !== null && end < this.draftStart) { return 'endBeforeStart'; }
    return null;
  }

  public onInput(): void {
    this.dirtyChange.emit(this.isDirty);
  }

  public onSave(): void {
    if (!this.isDirty || this.busy || this.error) { return; }
    this.save.emit({ start: this.draftStart, end: this.effectiveEnd });
  }

  public onCancel(): void {
    this.draftStart = this.start ?? '';
    this.draftEnd = this.end ?? '';
    this.current = this.end === null;
    this.dirtyChange.emit(false);
    this.cancel.emit();
  }

  public onKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this.onSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancel();
    }
  }
}
